import {cloneElement, useEffect, useRef} from 'react';
import ClickAwayListener from 'react-click-away-listener';
import {useLatest} from 'react-use';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  type OffsetOptions,
  type Placement,
  useFloating,
} from '@floating-ui/react';
import isChromatic from 'chromatic/isChromatic';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';
import {twMerge} from 'tailwind-merge';

type PopupProps = React.PropsWithChildren<{
  animationProps?: React.ComponentProps<typeof motion.div>;
  hideArrow?: boolean;
  id?: string;
  shouldAutoUpdate?: boolean;
  isControlled?: boolean;
  offset?: OffsetOptions;
  onPopupToggle: (open: boolean) => void;
  open: boolean;
  placement?: Placement;
  slotProps?: {
    arrow?: Omit<React.ComponentProps<typeof FloatingArrow>, 'context'>;
    popup?: React.HTMLAttributes<HTMLDivElement>;
    popupCard?: React.ComponentProps<'div'>;
    root?: React.ComponentProps<'div'>;
  };
  trigger: React.ReactElement;
  triggerTabIndex?: number | null;
}>;

// Round coordinates by device pixel ratio to prevent sub-pixel rendering issues
const roundByDPR = (value: number): number => {
  if (typeof window === 'undefined') return value;

  // In Chromatic: ALWAYS round to whole pixels for deterministic positioning
  // This ensures Floating UI coordinates like 123.4 and 123.6 both become 123
  if (isChromatic()) return Math.round(value);

  // In production: Use DPR-aware rounding for sharp rendering on retina displays
  const dpr = window.devicePixelRatio || 1;
  return Math.round(value * dpr) / dpr;
};

const Popup: React.FC<PopupProps> = ({
  animationProps,
  children,
  hideArrow = false,
  id = 'popup',
  shouldAutoUpdate = false,
  isControlled,
  offset: offsetProps = hideArrow ? 0 : 10,
  onPopupToggle,
  open,
  placement = 'bottom',
  slotProps = {},
  trigger,
  triggerTabIndex = 0,
  ...props
}) => {
  const arrowRef = useRef<SVGSVGElement | null>(null);

  const {
    refs,
    x,
    y,
    context,
    middlewareData,
    placement: resolvedPlacement,
  } = useFloating({
    // eslint-disable-next-line react-hooks/refs
    middleware: [arrow({element: arrowRef}), flip(), offset(offsetProps)],
    onOpenChange: onPopupToggle,
    open,
    placement,
    strategy: 'fixed',
    // In Chromatic, keep the position in sync with late layout (e.g. web fonts) so the
    // capture doesn't depend on when the popup first opened.
    whileElementsMounted: shouldAutoUpdate || isChromatic() ? autoUpdate : undefined,
  });

  // Apply DPR-aware rounding to prevent Chromatic flaky tests from sub-pixel positioning
  // Hide popup until coordinates are available to prevent flash at (0,0)
  const floatingStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    transform: `translate(${roundByDPR(x ?? 0)}px, ${roundByDPR(y ?? 0)}px)`,
    visibility: x == null || y == null ? 'hidden' : 'visible',
  };

  // Floating UI offsets the arrow from the unrounded popup position. In Chromatic, re-anchor it
  // to the rounded position above and snap it to a whole pixel so every capture matches.
  const isVerticalSide = ['top', 'bottom'].includes(resolvedPlacement.split('-')[0]);
  const arrowOffset = isVerticalSide ? middlewareData.arrow?.x : middlewareData.arrow?.y;
  const popupOffset = (isVerticalSide ? x : y) ?? 0;
  const snappedArrowOffset =
    arrowOffset == null ? null : Math.round(arrowOffset + popupOffset - roundByDPR(popupOffset));
  const chromaticArrowStyle: React.CSSProperties | undefined =
    isChromatic() && snappedArrowOffset != null
      ? isVerticalSide
        ? {left: snappedArrowOffset}
        : {top: snappedArrowOffset}
      : undefined;

  const typedTrigger = trigger as React.ReactElement<React.ComponentProps<'button'>>;
  const triggerElement = cloneElement(typedTrigger, {
    onClick: event => {
      if (typedTrigger.props.onClick) typedTrigger.props.onClick(event);
      onPopupToggle(!open);
    },
    onKeyDown: event => {
      if (typedTrigger.props.onKeyDown) typedTrigger.props.onKeyDown(event);
      if (event.key === 'Escape' && open) {
        // Keep Escape from dismissing a parent Modal / dialog.
        event.stopPropagation();
        onPopupToggle(false);
      }
    },
    tabIndex: triggerTabIndex ?? undefined,
    type: 'button',
    ref: refs.setReference,
  });

  const onPopupToggleRef = useLatest(onPopupToggle);
  useEffect(() => {
    if (!isControlled) onPopupToggleRef.current?.(open);
  }, [isControlled, open, onPopupToggleRef]);

  useEffect(() => {
    if (!open || isControlled) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Capture + stopImmediatePropagation so a parent Modal (e.g. Radix Dialog)
      // does not also dismiss when the user only meant to close this Popup.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onPopupToggle(false);
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open, isControlled, onPopupToggle]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open && !isControlled) onPopupToggle(false);
      }}
    >
      <div {...props} {...slotProps?.root}>
        {triggerElement}
        <AnimatePresence>
          {open && Boolean(refs.reference.current) && (
            <div
              id={open ? id : undefined}
              ref={refs.setFloating}
              role="tooltip"
              style={floatingStyles}
              tabIndex={-1}
              {...slotProps?.popup}
              className={twMerge('z-1300', slotProps?.popup?.className)}
            >
              <motion.div
                animate={{opacity: 1}}
                className="relative z-1300 rounded-xl border border-peppercorn-100 shadow-lg"
                exit={{opacity: 0}}
                initial={{opacity: 0}}
                transition={{duration: isChromatic() ? 0 : 0.1}}
                {...animationProps}
              >
                <>
                  {!hideArrow && (
                    <FloatingArrow
                      className="stroke-peppercorn-100"
                      context={context}
                      fill="white"
                      height={12}
                      ref={arrowRef}
                      strokeWidth={1}
                      tipRadius={2}
                      width={20}
                      {...slotProps?.arrow}
                      style={{...slotProps?.arrow?.style, ...chromaticArrowStyle}}
                    />
                  )}

                  <div
                    {...slotProps?.popupCard}
                    className={twMerge(
                      'rounded-[11px] bg-white p-6 text-peppercorn-800',
                      slotProps?.popupCard?.className,
                    )}
                  >
                    {children}
                  </div>
                </>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </ClickAwayListener>
  );
};

export default Popup;
